import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Users, Lock, Mail, User, Check, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCoreAuth } from '../contexts/CoreAuthContext';
import { TeamInvitationService } from '../lib/team-invitations';
import { supabase } from '../lib/supabase';

interface InvitationData {
  id: string;
  email: string;
  role: string;
  organizationId: string;
  organizationName: string;
  inviterName: string;
  status: string;
  expiresAt: string;
}

export default function InvitationAcceptancePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register, login, user, resendEmailConfirmation } = useAuth();
  const { verifyOtp } = useCoreAuth();

  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'validating' | 'account-setup' | 'emailConfirmation' | 'sign-in' | 'accepting' | 'onboarding' | 'complete'>('validating');
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);
  
  // Terms and Privacy acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  // Marketing consent state
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  // Email resend states
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  
  // OTP states (like in OnboardingPage)
  const [otpCode, setOtpCode] = useState<string[]>(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const token = searchParams.get('token');

  useEffect(() => {
    validateInvitation();
  }, [token]);

  // Check if user just verified email and came back
  useEffect(() => {
    if (user && step === 'sign-in') {
      // User successfully signed in after email verification
      setStep('accepting');
      setTimeout(() => {
        handleInvitationAcceptance();
      }, 500);
    }
  }, [user, step]);

  const validateInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link - no token provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const invitation = await TeamInvitationService.validateInvitationToken(token);
      
      if (!invitation) {
        setError('Invalid or expired invitation token');
        return;
      }

      if (invitation.status !== 'pending') {
        setError('This invitation has already been used or has expired');
        return;
      }

      setInvitationData(invitation);
      
      // Check if user is already signed in and email verified
      const { data: { session } } = await supabase.auth.getSession();
      if (session && session.user && session.user.email_confirmed_at) {
        // User is signed in and verified, just need to accept invitation
        setStep('accepting');
        setTimeout(() => {
          handleInvitationAcceptance();
        }, 500);
      } else if (session && session.user && !session.user.email_confirmed_at) {
        // User is signed in but email not verified
        setStep('emailConfirmation');
      } else {
        // User needs to create account or sign in
        setStep('account-setup');
      }
      
    } catch (err) {
      console.error('Error validating invitation:', err);
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearResendState = () => {
    setResendError(null);
    setResendSuccess(false);
    setResendLoading(false);
  };

  const handleResendEmail = async () => {
    if (!invitationData?.email) {
      setResendError('Email address is required');
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendSuccess(false);
    
    try {
      await resendEmailConfirmation(invitationData.email);
      setResendSuccess(true);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Resend email error:', error);
      
      // Handle specific error messages
      if (error.message?.includes('rate limit')) {
        setResendError('Too many requests. Please wait a few minutes before trying again.');
      } else if (error.message?.includes('email not found')) {
        setResendError('This email address was not found. Please check your email or register again.');
      } else if (error.message?.includes('already confirmed')) {
        setResendError('Your email is already confirmed. You can sign in now.');
        setStep('sign-in');
      } else {
        setResendError(error.message || 'Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleAccountCreation = async () => {
    if (!invitationData) return;

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!termsAccepted) {
      setTermsError('You must accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    try {
      setAcceptingInvitation(true);
      setError(null);

      console.log('🔧 Creating account for invitation:', {
        email: invitationData.email,
        name: `${firstName} ${lastName}`,
        organizationName: invitationData.organizationName,
        role: invitationData.role
      });

      // For invitation flow, prioritize creating new account
      // If user exists but is unverified, we'll handle that in the catch block
      console.log('🎫 Invitation flow: Attempting to create account for invited user');

      // Create account with the invited email
      await register(
        invitationData.email,
        password,
        `${firstName} ${lastName}`,
        undefined,
        termsAccepted,
        marketingConsent
      );

      console.log('✅ Account created successfully, moving to email confirmation');
      
      // Move to email confirmation step with OTP
      setStep('emailConfirmation');
      
    } catch (err: any) {
      console.error('Error creating account:', err);
      
      // Handle duplicate email error - prioritize invitation flow
      if (err.message && (
        err.message.toLowerCase().includes('user already registered') || 
        err.message.toLowerCase().includes('duplicate') ||
        err.name === 'DuplicateEmailError'
      )) {
        console.log('🎫 INVITATION FLOW: User exists, but this is an invitation - trying to recover');
        
        // For invitation flow, try to clear the existing unverified user and recreate
        try {
          console.log('🧹 Attempting to clean up existing unverified user for invitation');
          
          // Try to sign in first to see what kind of existing user we have
          await login(invitationData.email, password);
          
          // If login succeeds, check email verification
          const { data: { session } } = await supabase.auth.getSession();
          if (session && session.user) {
            if (session.user.email_confirmed_at) {
              // User exists and is verified - proceed to accept invitation
              console.log('✅ User exists and is verified - proceeding to invitation');
              setStep('accepting');
              setTimeout(() => {
                handleInvitationAcceptance();
              }, 500);
              return;
            } else {
              // User exists but not verified - proceed to email confirmation
              console.log('📧 User exists but not verified - proceeding to email confirmation');
              setStep('emailConfirmation');
              return;
            }
          }
        } catch (loginError: any) {
          console.log('🎫 Login failed during invitation recovery:', loginError.message);
          
          if (loginError.message?.includes('Email not confirmed')) {
            // User exists but email not confirmed - this is common in invitations
            console.log('📧 User exists with unconfirmed email - treating as invitation signup');
            setError('We found an existing account that needs verification. Please verify your email to continue with the invitation.');
            setStep('emailConfirmation');
            return;
          } else if (loginError.message?.includes('Invalid login credentials')) {
            // User exists but wrong password - they need to use existing account
            console.log('❌ User exists with different password');
            setError('An account with this email already exists. Please use the "Sign In" option below with your existing password, or contact support if you forgot your password.');
            setStep('sign-in');
            return;
          }
        }
        
        // If all else fails, show clear message about existing account
        console.log('🚨 Could not resolve duplicate account issue');
        setError('An account with this email already exists. Please sign in with your existing account to accept the invitation.');
        setStep('sign-in');
        return;
      }
      
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setAcceptingInvitation(false);
    }
  };

  const handleSignIn = async () => {
    if (!invitationData?.email || !password) {
      setError('Please enter your password');
      return;
    }

    try {
      setAcceptingInvitation(true);
      setError(null);

      await login(invitationData.email, password);
      
      console.log('✅ User signed in successfully');
      
      // After sign in, check if email is verified
      const { data: { session } } = await supabase.auth.getSession();
      if (session && session.user && session.user.email_confirmed_at) {
        // Email verified, proceed to accept invitation
        setStep('accepting');
        setTimeout(() => {
          handleInvitationAcceptance();
        }, 500);
      } else {
        // Email not verified, move to email confirmation step
        setStep('emailConfirmation');
      }
      
    } catch (err: any) {
      console.error('Error signing in:', err);
      
      if (err.message?.includes('Invalid login credentials')) {
        setError('Invalid password. Please check your password.');
      } else if (err.message?.includes('Email not confirmed')) {
        setError('Please verify your email address first.');
        setStep('emailConfirmation');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setAcceptingInvitation(false);
    }
  };

  // OTP Handler Functions (like in OnboardingPage)
  const handleOtpInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...otpCode];
    newCode[index] = value.slice(-1); // Only take the last digit
    setOtpCode(newCode);
    setOtpError(null); // Clear error when user starts typing

    // Auto-advance to next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleEmailConfirmationOtpVerify(newCode.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setOtpCode(newCode);
      otpInputRefs.current[5]?.focus();
      handleEmailConfirmationOtpVerify(pastedData);
    }
  };

  const handleEmailConfirmationOtpVerify = async (token: string) => {
    if (token.length !== 6) return;
    
    setIsVerifyingOtp(true);
    setOtpError(null);
    
    try {
      await verifyOtp(invitationData?.email || '', token, 'signup');
      
      // After successful verification, proceed to accept invitation
      console.log('🔄 Email confirmation OTP verified, proceeding to accept invitation');
      setStep('accepting');
      setTimeout(() => {
        handleInvitationAcceptance();
      }, 500);
    } catch (error: any) {
      console.error('Email confirmation OTP verification error:', error);
      setOtpError(error.message || 'Invalid verification code. Please try again.');
      // Clear the code inputs
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleInvitationAcceptance = async () => {
    if (!invitationData || !token) return;

    try {
      setAcceptingInvitation(true);
      setError(null);

      console.log('🔧 Accepting team invitation:', {
        token: token,
        email: invitationData.email,
        role: invitationData.role,
        organizationId: invitationData.organizationId
      });

      // Check if user is authenticated and email verified
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        throw new Error('Please sign in first to accept the invitation');
      }

      if (!session.user.email_confirmed_at) {
        throw new Error('Please verify your email address first');
      }

      // Accept the invitation
      await TeamInvitationService.acceptTeamInvitation(token);
      
      console.log('✅ Team invitation accepted successfully');
      
      // Refresh user profile to include organization data
      console.log('🔄 Refreshing user profile with organization data');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay for database consistency
      
      // Force a page reload to ensure fresh data
      console.log('🔄 Reloading page to refresh user context');
      window.location.href = '/dashboard';
      
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAcceptingInvitation(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Invalid Invitation
          </h1>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Sign In
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <Users className="w-16 h-16 text-purple-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Team Invitation
          </h1>
          {invitationData && (
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                You're Invited!
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                You've been invited to join <span className="font-semibold text-blue-600">
                  {invitationData.organizationName || 'a team organization'}
                </span> as a <span className="font-semibold">{invitationData.role}</span>
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Invited by {invitationData.inviterName || 'a team administrator'}
              </p>
            </div>
          )}
        </div>

        {/* Account Setup Step */}
        {step === 'account-setup' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Create Your Account</h2>
              <p className="text-sm text-gray-600">Set up your account to join the team</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={invitationData?.email || ''}
                    disabled
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Terms and Privacy Acceptance */}
              <div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="terms-acceptance"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        if (e.target.checked) {
                          setTermsError(null);
                        }
                      }}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                  </div>
                  <label htmlFor="terms-acceptance" className="text-sm text-gray-700 leading-relaxed">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/terms', '_blank')}
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      Terms of Service
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/privacy', '_blank')}
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>
                {termsError && (
                  <p className="mt-2 text-xs text-red-600">{termsError}</p>
                )}
              </div>

              {/* Marketing Consent */}
              <div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="marketing-consent"
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                  </div>
                  <label htmlFor="marketing-consent" className="text-sm text-gray-700 leading-relaxed">
                    I want to receive product updates, tips, and special offers via email (optional)
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleAccountCreation}
                disabled={acceptingInvitation}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {acceptingInvitation ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Creating Account...</span>
                  </>
                ) : (
                  <span>Create Account & Join Team</span>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => setStep('sign-in')}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  Already have an account? Sign in first
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Confirmation Step */}
        {step === 'emailConfirmation' && (
          <div className="space-y-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Complete Your Invitation
              </h2>
              <p className="text-gray-600 mb-4">
                We've sent a verification code to <strong>{invitationData?.email}</strong>. 
                Please click the link in your email OR enter the 6-digit code below to verify your account and join <span className="font-semibold text-purple-600">{invitationData?.organizationName}</span>.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-purple-700">
                  👥 You're joining as a <strong>{invitationData?.role}</strong> - almost done!
                </p>
              </div>
            </div>
            
            {/* OTP Input Section */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-sm text-gray-500 font-medium">Enter verification code</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
              
              <div className="flex justify-center space-x-2 mb-4">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={el => {
                      if (!otpInputRefs.current) otpInputRefs.current = [];
                      otpInputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    value={otpCode[index] || ''}
                    onChange={(e) => handleOtpInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    className={`w-10 h-10 sm:w-10 sm:h-12 text-center text-base sm:text-lg font-bold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      otpError 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                    } ${otpCode[index] ? 'bg-purple-50 border-purple-300' : 'bg-white'}`}
                    maxLength={1}
                    disabled={isVerifyingOtp}
                  />
                ))}
              </div>

              {otpError && (
                <div className="text-center mb-4">
                  <p className="text-sm text-red-600">{otpError}</p>
                </div>
              )}

              {isVerifyingOtp && (
                <div className="flex items-center justify-center space-x-2 text-gray-600 text-sm mb-4">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying code...</span>
                </div>
              )}
            </div>

            {/* Success message for resend */}
            {resendSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4"
              >
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <p className="text-sm text-green-700 font-medium">
                    Verification email sent successfully! Please check your inbox.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error message for resend */}
            {resendError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
              >
                <div className="flex items-center space-x-2">
                  <X className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{resendError}</p>
                </div>
              </motion.div>
            )}

            {/* Resend email button */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3 text-center">
                Didn't receive the email?
              </p>
              <button
                onClick={handleResendEmail}
                disabled={resendLoading || resendSuccess}
                className={`w-full py-3 rounded-lg font-medium transition-all ${
                  resendLoading || resendSuccess
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {resendLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : resendSuccess ? (
                  <div className="flex items-center justify-center space-x-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Email sent!</span>
                  </div>
                ) : (
                  'Resend verification email'
                )}
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setStep('sign-in');
                  setError(null);
                  clearResendState();
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105"
              >
                Continue to Sign In
              </button>
              
              <button
                onClick={() => {
                  setStep('account-setup');
                  clearResendState();
                }}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium block w-full text-center"
              >
                Back to Account Setup
              </button>
            </div>
          </div>
        )}


        {/* Sign In Step */}
        {step === 'sign-in' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign In</h2>
              <p className="text-sm text-gray-600">Sign in to your account</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={invitationData?.email || ''}
                    disabled
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleSignIn}
                disabled={acceptingInvitation}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {acceptingInvitation ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => setStep('account-setup')}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  I don't have an account, take me back to account setup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Accepting Invitation Step */}
        {step === 'accepting' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Join Team</h2>
              <p className="text-sm text-gray-600">Accept your invitation to get started</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <button
              onClick={handleInvitationAcceptance}
              disabled={acceptingInvitation}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {acceptingInvitation ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Joining Team...</span>
                </>
              ) : (
                <span>Accept Invitation</span>
              )}
            </button>
          </div>
        )}

        {/* Onboarding Step */}
        {step === 'onboarding' && (
          <div className="space-y-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Welcome to the Team!</h2>
              <p className="text-sm text-gray-600">
                You've successfully joined {invitationData?.organizationName} as a {invitationData?.role}.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-medium text-purple-900 mb-2">What's Next?</h3>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Explore your team's projects and analyses</li>
                <li>• Collaborate on creative compliance reviews</li>
                <li>• Access shared insights and reports</li>
                <li>• Contribute to team success!</li>
              </ul>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="space-y-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">All Set!</h2>
              <p className="text-sm text-gray-600">
                Redirecting you to the dashboard...
              </p>
            </div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        )}
      </motion.div>
    </div>
  );
} 